import { FC, useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@features/ui';
import { getTopSellingProducts } from '@src/services/reports.ts';
import {
  TReportTopSellingProductsRes,
  TTopSellingProductItem,
} from '@src/types/reports';
import { formatCurrency, centsToUSD } from '@lib/utils';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

interface TopSellingProductsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  startDate?: string | null;
  endDate?: string | null;
}

export const TopSellingProductsDialog: FC<TopSellingProductsDialogProps> = ({
  open,
  onOpenChange,
  startDate,
  endDate,
}) => {
  const [data, setData] = useState<TReportTopSellingProductsRes | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open, startDate, endDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await getTopSellingProducts({
        start_date: startDate || null,
        end_date: endDate || null,
      });

      setData(result);
    } catch (error) {
      console.error('Error fetching top selling products:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Top Selling Products</DialogTitle>
          <DialogDescription>
            Top 10 best-performing products by quantity sold
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <p className="text-muted-foreground">Loading...</p>
          </div>
        ) : data ? (
          <div className="space-y-6">
            <div className="rounded-lg bg-card-stat p-4">
              <p className="text-sm text-muted-foreground mb-1">
                Total Products Sold
              </p>
              <p className="text-2xl font-semibold">
                {data.total_products_sold.toLocaleString()}
              </p>
            </div>

            {/* Top 10 Products by Quantity Sold Bar Chart */}
            <div className="space-y-2">
              <h3 className="text-lg font-semibold">
                Top 10 Products by Quantity Sold
              </h3>
              <div className="h-80 w-full rounded-lg bg-card-stat p-4">
                <ResponsiveContainer height="100%" width="100%">
                  <BarChart data={data.top_products} layout="vertical">
                    <CartesianGrid stroke="#333" strokeDasharray="3 3" />
                    <XAxis
                      tick={{ fill: '#888', fontSize: 12 }}
                      type="number"
                    />
                    <YAxis
                      dataKey="package_name"
                      tick={{ fill: '#888', fontSize: 11 }}
                      type="category"
                      width={150}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1a1a1a',
                        border: '1px solid #333',
                        borderRadius: '8px',
                      }}
                      cursor={{ fill: 'transparent' }}
                      formatter={(value: number) => [
                        value.toLocaleString(),
                        'Quantity Sold',
                      ]}
                    />
                    <Bar
                      dataKey="quantity_sold"
                      fill="#1890FF"
                      radius={[0, 8, 8, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Top 10 Products by Revenue Bar Chart */}
            <div className="space-y-2">
              <h3 className="text-lg font-semibold">
                Top 10 Products by Revenue
              </h3>
              <div className="h-80 w-full rounded-lg bg-card-stat p-4">
                <ResponsiveContainer height="100%" width="100%">
                  <BarChart data={data.top_products} layout="vertical">
                    <CartesianGrid stroke="#333" strokeDasharray="3 3" />
                    <XAxis
                      tick={{ fill: '#888', fontSize: 12 }}
                      tickFormatter={(value) =>
                        `$${formatCurrency(centsToUSD(value), 0)}`
                      }
                      type="number"
                    />
                    <YAxis
                      dataKey="package_name"
                      tick={{ fill: '#888', fontSize: 11 }}
                      type="category"
                      width={150}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1a1a1a',
                        border: '1px solid #333',
                        borderRadius: '8px',
                      }}
                      cursor={{ fill: 'transparent' }}
                      formatter={(value: number) => [
                        `$${formatCurrency(centsToUSD(value), 2)}`,
                        'Revenue',
                      ]}
                    />
                    <Bar
                      dataKey="revenue"
                      fill="#52C41A"
                      radius={[0, 8, 8, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Average Price of Top 10 Products Bar Chart */}
            <div className="space-y-2">
              <h3 className="text-lg font-semibold">
                Average Price of Top 10 Products
              </h3>
              <div className="h-80 w-full rounded-lg bg-card-stat p-4">
                <ResponsiveContainer height="100%" width="100%">
                  <BarChart data={data.top_products} layout="vertical">
                    <CartesianGrid stroke="#333" strokeDasharray="3 3" />
                    <XAxis
                      tick={{ fill: '#888', fontSize: 12 }}
                      tickFormatter={(value) =>
                        `$${formatCurrency(centsToUSD(value), 0)}`
                      }
                      type="number"
                    />
                    <YAxis
                      dataKey="package_name"
                      tick={{ fill: '#888', fontSize: 11 }}
                      type="category"
                      width={150}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1a1a1a',
                        border: '1px solid #333',
                        borderRadius: '8px',
                      }}
                      cursor={{ fill: 'transparent' }}
                      formatter={(value: number) => [
                        `$${formatCurrency(centsToUSD(value), 2)}`,
                        'Avg Price',
                      ]}
                    />
                    <Bar
                      dataKey="average_price"
                      fill="#FAAD14"
                      radius={[0, 8, 8, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Data Table */}
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 text-sm font-medium w-8">#</th>
                    <th className="text-left p-3 text-sm font-medium">
                      Product Name
                    </th>
                    <th className="text-right p-3 text-sm font-medium">
                      Quantity Sold
                    </th>
                    <th className="text-right p-3 text-sm font-medium">
                      Revenue
                    </th>
                    <th className="text-right p-3 text-sm font-medium">
                      Avg Price
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.top_products.map(
                    (item: TTopSellingProductItem, index: number) => (
                      <tr key={item.package_id} className="hover:bg-muted/30">
                        <td className="p-3 text-sm text-muted-foreground">
                          {index + 1}
                        </td>
                        <td className="p-3 text-sm font-medium">
                          {item.package_name}
                        </td>
                        <td className="p-3 text-sm text-right">
                          {item.quantity_sold.toLocaleString()}
                        </td>
                        <td className="p-3 text-sm text-right font-medium">
                          ${formatCurrency(centsToUSD(item.revenue), 2)}
                        </td>
                        <td className="p-3 text-sm text-right">
                          ${formatCurrency(centsToUSD(item.average_price), 2)}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center py-8">
            <p className="text-muted-foreground">No data available</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
